import React from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Box, Button, ButtonBase, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { Building2, Check, Eye, EyeOff, Network, ShieldCheck, TriangleAlert, Users } from 'lucide-react';
import CalendarIntegration from './CalendarIntegration';
import UserAccess from './UserAccess';
import SetupHealth, { useEmployeeSetupAudit } from './SetupHealth';
import { useAuth } from '../../contexts/AuthContext';
import { AUTH_ROLE_LABELS, AuthRole } from '../../utils/roles';
import { CountBadge, PageHeader, SectionCard, StatusPill } from '../../components/ui';
import { focusRing, sbg, transition } from '../../theme/sbgTokens';
import { ROLE_OPTIONS, ROLE_SUMMARY, SETTINGS_ROLES } from './settings-parts/roles';
import { SETTINGS_TAB_LABELS } from '../../utils/settingsTabs';

const previewRoles: AuthRole[] = ROLE_OPTIONS;

/**
 * Setup health tab marker: an issue count, or a warning glyph when the check itself failed.
 * Each one carries its meaning in words: a tooltip for sighted users and an image label in
 * the tab's accessible name, so "critical" never rests on the red tint alone.
 */
const SetupHealthMarker: React.FC<{ failed: boolean; flagged: number; critical: number }> = ({ failed, flagged, critical }) => {
  if (failed) {
    const label = 'Setup check incomplete';
    return (
      <Tooltip title={label}>
        <Box
          component="span"
          role="img"
          aria-label={label}
          sx={{
            display: 'inline-grid',
            placeItems: 'center',
            width: 18,
            height: 18,
            borderRadius: sbg.radius.pill,
            bgcolor: sbg.status.warning.bg,
            color: sbg.status.warning.icon,
            boxShadow: `inset 0 0 0 1px ${sbg.status.warning.border}`,
          }}
        >
          <TriangleAlert size={12} aria-hidden="true" />
        </Box>
      </Tooltip>
    );
  }
  if (flagged === 0) return null;
  const label = `${flagged} setup ${flagged === 1 ? 'issue' : 'issues'}${critical > 0 ? `, ${critical} critical` : ''}`;
  // The span takes the Tooltip's ref (CountBadge doesn't forward one) and names the marker in the tab.
  return (
    <Tooltip title={label}>
      <Box component="span" role="img" aria-label={label} sx={{ display: 'inline-flex' }}>
        <CountBadge count={flagged} tone={critical > 0 ? 'danger' : 'neutral'} label={label} />
      </Box>
    </Tooltip>
  );
};

/** One selectable role in the preview picker: a full-width row (book §11.8, selectable card). */
const RoleOption: React.FC<{ role: AuthRole; selected: boolean; onToggle: () => void }> = ({ role, selected, onToggle }) => {
  const summaryId = `role-preview-${role}-summary`;
  const noSettings = !SETTINGS_ROLES.includes(role);
  return (
    <ButtonBase
      aria-pressed={selected}
      aria-label={AUTH_ROLE_LABELS[role]}
      aria-describedby={summaryId}
      onClick={onToggle}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 2,
        py: 1.5,
        minHeight: 56,
        textAlign: 'left',
        borderRadius: sbg.radius.lg,
        border: `1px solid ${selected ? sbg.action.primary : sbg.border.default}`,
        boxShadow: selected ? `inset 0 0 0 1px ${sbg.action.primary}` : 'none',
        bgcolor: selected ? sbg.bg.selected : sbg.bg.surface,
        transition: transition('border-color', 'background-color', 'box-shadow'),
        '&:hover': selected ? undefined : { borderColor: sbg.border.strong, boxShadow: sbg.shadow.sm },
        '&.Mui-focusVisible': focusRing,
      }}
    >
      <Box sx={{ display: 'grid', minWidth: 0 }}>
        <Typography variant="labelMd" component="span" sx={{ color: sbg.text.primary }}>
          {AUTH_ROLE_LABELS[role]}
        </Typography>
        <Typography id={summaryId} variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
          {ROLE_SUMMARY[role]}
          {noSettings ? ' · No access to Settings' : ''}
        </Typography>
      </Box>
      {selected && (
        <StatusPill tone="info" icon={<Check aria-hidden="true" />}>
          Previewing
        </StatusPill>
      )}
    </ButtonBase>
  );
};

const PREVIEW_FACTS: { icon: React.ReactNode; title: string; body: string }[] = [
  { icon: <Eye size={16} />, title: 'Changes what you see', body: 'Navigation and screens switch to the role you pick.' },
  { icon: <ShieldCheck size={16} />, title: 'Keeps your permissions', body: 'Requests still run with your administrator access.' },
  { icon: <EyeOff size={16} />, title: 'Not a data check', body: 'It does not test another user’s data access.' },
];

const RolePreviewPane: React.FC<{ viewAsRole: AuthRole | null; setViewAsRole: (role: AuthRole | null) => void }> = ({
  viewAsRole,
  setViewAsRole,
}) => (
  <Box
    sx={{
      display: 'grid',
      gap: 3,
      alignItems: 'start',
      gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(280px, 1fr)' },
    }}
  >
    <SectionCard
      id="role-preview"
      title="Preview a role"
      description="See the navigation and screens available to each role. Your account keeps its administrator permissions when making requests."
      footer={
        viewAsRole ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', width: '100%' }}>
            <Typography variant="body1" sx={{ color: sbg.text.primary }}>
              Previewing {AUTH_ROLE_LABELS[viewAsRole]}
            </Typography>
            <Button variant="outlined" size="small" startIcon={<EyeOff />} onClick={() => setViewAsRole(null)}>
              Exit role preview
            </Button>
          </Box>
        ) : undefined
      }
    >
      <Box role="group" aria-label="Roles" sx={{ display: 'grid', gap: 1 }}>
        {previewRoles.map((role) => (
          <RoleOption
            key={role}
            role={role}
            selected={viewAsRole === role}
            onToggle={() => setViewAsRole(viewAsRole === role ? null : role)}
          />
        ))}
      </Box>
    </SectionCard>

    <SectionCard title="How preview works" dense>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 2 }}>
        {PREVIEW_FACTS.map((fact) => (
          <Box component="li" key={fact.title} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Box aria-hidden="true" sx={{ color: sbg.text.secondary, display: 'flex', pt: '2px' }}>
              {fact.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="labelMd" component="p" sx={{ color: sbg.text.primary }}>
                {fact.title}
              </Typography>
              <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
                {fact.body}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
      <Typography variant="body2" sx={{ color: sbg.text.secondary, mt: 2, pt: 2, borderTop: `1px solid ${sbg.border.subtle}` }}>
        Manager, Jr manager and Employee can’t open Settings. While you preview one of them, exit from the preview bar at
        the top of the page.
      </Typography>
    </SectionCard>
  </Box>
);

const AdminPanel: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { viewAsRole, setViewAsRole, actualRole } = useAuth();
  const audit = useEmployeeSetupAudit();
  const auditFailed = Boolean(audit.employeesError || audit.profilesError || audit.departmentsError);
  const flaggedCount = audit.criticalCount + audit.warningCount;
  const tabs = [
    // Labels live in utils/settingsTabs.ts so every pointer to a tab (no-access page,
    // employee form, org chart, Setup health fixes) names one that exists.
    { key: 'access', label: SETTINGS_TAB_LABELS.access },
    { key: 'setup-health', label: SETTINGS_TAB_LABELS['setup-health'] },
    { key: 'calendar', label: SETTINGS_TAB_LABELS.calendar },
    ...(actualRole === 'admin' ? [{ key: 'preview', label: SETTINGS_TAB_LABELS.preview }] : []),
  ];
  const requestedTab = searchParams.get('tab');
  const tab = tabs.some((item) => item.key === requestedTab) ? requestedTab! : 'access';

  const changeTab = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next);
  };

  return (
    <Box>
      <PageHeader
        overline="Tools"
        title="Settings"
        meta={[
          'Organization-wide',
          'Admin and HR only',
          actualRole ? `Your access: ${AUTH_ROLE_LABELS[actualRole]}` : null,
        ]}
        actions={
          <Box component="nav" aria-label="Organization shortcuts" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Button component={RouterLink} to="/employees" size="small" startIcon={<Users />}>
              Employees
            </Button>
            <Button component={RouterLink} to="/departments" size="small" startIcon={<Building2 />}>
              Departments
            </Button>
            <Button component={RouterLink} to="/org-chart" size="small" startIcon={<Network />}>
              Org chart
            </Button>
          </Box>
        }
      >
        <Tabs
          value={tab}
          onChange={(_, value) => changeTab(value)}
          aria-label="Settings sections"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {tabs.map((item) => (
            <Tab
              key={item.key}
              value={item.key}
              id={`settings-tab-${item.key}`}
              aria-controls={`settings-panel-${item.key}`}
              label={
                item.key === 'setup-health' ? (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                    {item.label}
                    {!audit.isLoading && (
                      <SetupHealthMarker failed={auditFailed} flagged={flaggedCount} critical={audit.criticalCount} />
                    )}
                  </Box>
                ) : (
                  item.label
                )
              }
            />
          ))}
        </Tabs>
      </PageHeader>

      <Box role="tabpanel" id={`settings-panel-${tab}`} aria-labelledby={`settings-tab-${tab}`}>
        {tab === 'access' && <UserAccess />}
        {tab === 'setup-health' && <SetupHealth />}
        {tab === 'calendar' && <CalendarIntegration />}
        {tab === 'preview' && actualRole === 'admin' && <RolePreviewPane viewAsRole={viewAsRole} setViewAsRole={setViewAsRole} />}
      </Box>
    </Box>
  );
};

export default AdminPanel;
